import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, ArrowUp, Sparkle, RotateCcw, Phone, Mail } from "lucide-react";
import BrandLogo from "./BrandLogo";
import { principals } from "../../data/mindscope";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "mindscope.chat.sessionId";

const SUGGESTIONS = [
  "How does an IPO differ from a Direct Listing?",
  "When would we choose QIP over a Rights Issue?",
  "What triggers a Buy Back mandate?",
  "Explain SME to Main Board migration.",
];

const WELCOME_MSG = {
  role: "assistant",
  content:
    "Welcome. I am Ask Mindscope — a digital extension of our principals. Ask about any of our capital solutions, or the shape of a mandate you are contemplating.",
};

export default function AskMindscope() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Restore session from localStorage and load transcript
  useEffect(() => {
    let cancelled = false;
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!stored) return;
    setSessionId(stored);
    (async () => {
      try {
        const res = await fetch(`${API}/chat/history?session_id=${encodeURIComponent(stored)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages([
            WELCOME_MSG,
            ...data.messages.map((m) => ({ role: m.role, content: m.content })),
          ]);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist sessionId whenever it changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionId) window.localStorage.setItem(STORAGE_KEY, sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const resetConversation = () => {
    if (busy) return;
    if (abortRef.current) abortRef.current.abort();
    if (typeof window !== "undefined") window.localStorage.removeItem(STORAGE_KEY);
    setSessionId(null);
    setMessages([WELCOME_MSG]);
    setInput("");
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setBusy(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionId }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          if (!data) continue;
          let parsed = null;
          try {
            parsed = JSON.parse(data);
          } catch {
            // ignore malformed frame
            continue;
          }
          if (event === "session" && parsed.session_id) {
            setSessionId(parsed.session_id);
          } else if (event === "delta" && parsed.text) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              if (last && last.role === "assistant") {
                copy[copy.length - 1] = { ...last, content: last.content + parsed.text };
              }
              return copy;
            });
          } else if (event === "error") {
            throw new Error(parsed.error || "Stream error");
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant" && !last.content) {
            copy[copy.length - 1] = {
              ...last,
              content:
                "Apologies — a transient issue interrupted the response. Please try again in a moment, or reach us via the Contact page.",
            };
          }
          return copy;
        });
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ask-launcher"
        aria-label={open ? "Close Ask Mindscope" : "Open Ask Mindscope"}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 md:w-16 md:h-16 rounded-full bg-ms-gold text-ms-navy shadow-[0_18px_40px_-15px_rgba(201,169,97,0.7)] flex items-center justify-center transition-transform duration-500 hover:-translate-y-1"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <X size={22} strokeWidth={2} />
            </motion.span>
          ) : (
            <motion.span
              key="m"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="flex items-center justify-center"
            >
              <MessageSquare size={22} strokeWidth={1.6} />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-ms-orange pulse-gold" />
        )}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.aside
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="ask-panel"
            className="fixed z-[59] bottom-24 right-4 md:right-6 w-[min(94vw,420px)] h-[min(80vh,620px)] bg-[#0A1128] border border-ms-gold/25 rounded-sm shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 bg-[#050914]">
              <BrandLogo variant="mark" className="h-7 w-auto" />
              <div className="flex-1">
                <div className="font-display text-lg text-white leading-none">
                  Ask <span className="italic text-ms-gold">Mindscope</span>
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-[0.6rem] tracking-[0.24em] uppercase text-ms-gold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-gold" />
                Online
              </span>
              <button
                onClick={resetConversation}
                data-testid="ask-reset"
                aria-label="Reset conversation"
                title="New conversation"
                className="ml-2 w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-ms-gold hover:border-ms-gold transition-colors duration-300"
              >
                <RotateCcw size={14} strokeWidth={1.6} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="ask-messages"
              className="flex-1 overflow-y-auto px-5 py-5 space-y-4 grain relative"
              style={{ scrollbarWidth: "thin" }}
            >
              {messages.map((m, i) => (
                <Message key={i} role={m.role} content={m.content} typing={busy && i === messages.length - 1 && m.role === "assistant" && !m.content} />
              ))}

              {/* Direct-connect band — appears once there's a real assistant reply, so visitor can hand off to a principal */}
              {!busy && messages.length > 1 && messages[messages.length - 1].role === "assistant" && messages[messages.length - 1].content && (
                <div data-testid="ask-direct-connect" className="pt-3">
                  <div className="rounded-lg border border-ms-gold/25 bg-ms-gold/[0.06] p-4">
                    <div className="text-[0.6rem] tracking-[0.28em] uppercase text-ms-gold mb-3">
                      Prefer a principal?
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {principals.map((p) => (
                        <a
                          key={p.email}
                          href={p.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`ask-direct-whatsapp-${p.name.split(' ')[0].toLowerCase()}`}
                          className="flex flex-col items-start gap-1 rounded-md bg-[#25D366]/10 hover:bg-[#25D366] hover:text-ms-navy border border-[#25D366]/40 px-3 py-2 transition-colors duration-300 text-[#25D366] hover:text-ms-navy"
                        >
                          <span className="text-[0.6rem] tracking-[0.22em] uppercase">WhatsApp</span>
                          <span className="font-display text-sm">{p.name.split(' ')[0]}</span>
                        </a>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {principals.map((p) => (
                        <a
                          key={p.email}
                          href={`mailto:${p.email}`}
                          data-testid={`ask-direct-email-${p.name.split(' ')[0].toLowerCase()}`}
                          className="flex items-center gap-2 rounded-md border border-ms-gold/30 hover:border-ms-gold hover:bg-ms-gold hover:text-ms-navy px-3 py-2 transition-colors duration-300 text-ms-gold"
                        >
                          <Mail size={12} strokeWidth={1.8} />
                          <span className="text-[0.7rem] truncate">{p.email}</span>
                        </a>
                      ))}
                    </div>
                    <a
                      href={`tel:${principals[0].tel}`}
                      data-testid="ask-direct-call"
                      className="mt-2 flex items-center justify-center gap-2 rounded-md border border-white/15 hover:border-ms-gold text-white/85 hover:text-ms-gold px-3 py-2 transition-colors duration-300"
                    >
                      <Phone size={12} strokeWidth={1.8} />
                      <span className="text-[0.7rem] tracking-[0.22em] uppercase">Call a Principal Directly</span>
                    </a>
                  </div>
                </div>
              )}

              {messages.length === 1 && (
                <div className="pt-2">
                  <div className="text-[0.6rem] tracking-[0.28em] uppercase text-white/40 mb-3 flex items-center gap-2">
                    <Sparkle size={12} className="text-ms-gold" /> Try
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        data-testid="ask-suggestion"
                        onClick={() => send(s)}
                        className="text-left text-xs md:text-[0.78rem] font-body text-white/80 border border-white/15 hover:border-ms-gold hover:text-ms-gold rounded-full px-3 py-2 transition-colors duration-300"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3 flex items-end gap-2 bg-[#050914]"
              data-testid="ask-composer"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Ask about IPO, QIP, Delisting…"
                data-testid="ask-input"
                className="flex-1 resize-none bg-transparent outline-none font-body text-sm text-white placeholder:text-white/35 px-3 py-2 max-h-32"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="ask-send"
                aria-label="Send"
                className="w-10 h-10 rounded-full bg-ms-gold text-ms-navy flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ms-gold-bright transition-colors duration-300"
              >
                <ArrowUp size={18} strokeWidth={2} />
              </button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

function Message({ role, content, typing }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] font-body text-[0.9rem] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-ms-gold text-ms-navy px-4 py-2.5 rounded-2xl rounded-br-sm"
            : "text-white/90 border border-white/10 bg-white/[0.03] px-4 py-3 rounded-2xl rounded-bl-sm"
        }`}
      >
        {typing ? (
          <span className="inline-flex gap-1 items-center py-1">
            <Dot delay={0} /><Dot delay={0.15} /><Dot delay={0.3} />
          </span>
        ) : (
          content
        )}
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-ms-gold inline-block"
      animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
      transition={{ duration: 1.1, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );
}
